import React, { useState, useEffect } from 'react';
import './App.css';
import {
  Menu, X, MapPin, Phone, Mail, ChevronRight, Settings,
  Factory, ShieldCheck, CheckCircle, ArrowRight, Wrench,
  Award, Clock, HardHat, Cog, Globe, Target, Ruler,
  Microscope, Cylinder, Linkedin, Download
} from 'lucide-react';

export default function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const logo = require('./assets/logo.png');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About Us' },
    { id: 'capabilities', label: 'Capabilities' },
    { id: 'industries', label: 'Industries' },
    { id: 'products', label: 'Products' },
    { id: 'quality', label: 'Quality' },
    { id: 'contact', label: 'Contact' }
  ];

  const handleNavClick = (id) => {
    setActiveTab(id);
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-[#334155] flex flex-col">
      {/* Top Bar - Brand Dark Blue */}
      <div className="bg-brand-blue text-slate-200 py-2 px-4 md:px-8 text-xs md:text-sm flex flex-col md:flex-row justify-between items-center z-20">
        <div className="flex items-center space-x-4 mb-2 md:mb-0">
          <span className="flex items-center hover:text-white transition cursor-pointer"><Phone className="w-3.5 h-3.5 mr-1.5 text-brand-lime" /> +91 98500 99243</span>
          <span className="flex items-center hover:text-white transition cursor-pointer"><Mail className="w-3.5 h-3.5 mr-1.5 text-brand-lime" /> enquiry@bjperfectworks.com</span>
        </div>
        <div className="flex items-center space-x-4">
          <span className="hidden md:flex items-center"><Globe className="w-3.5 h-3.5 mr-1.5 text-brand-lime" /> Exporting to USA & Global Markets</span>
          <span className="flex items-center"><MapPin className="w-3.5 h-3.5 mr-1.5 text-brand-lime" /> Pune, India</span>
          <a href="https://linkedin.com/company/bj-perfect-works" target="_blank" rel="noopener noreferrer" className="flex items-center text-brand-lime hover:text-white transition cursor-pointer">
            <Linkedin className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`bg-white sticky top-0 z-30 transition-shadow duration-300 ${isScrolled ? 'shadow-md' : 'border-b border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex justify-between items-center h-20 md:h-24">
            {/* Logo */}
            <div
              className="flex items-center cursor-pointer group"
              onClick={() => handleNavClick('home')}
            >
              <img src={logo} alt="B J Perfect Works Logo" className="h-12 md:h-14 w-auto mr-3 object-contain group-hover:opacity-90 transition" />
              <div className="border-l-2 border-slate-200 pl-2 sm:pl-3">
                <h1 className="text-xl sm:text-2xl md:text-4xl font-black text-brand-lime tracking-tight leading-none uppercase" style={{ textShadow: '0px 0px 1px rgba(0,0,0,0.1)' }}>B J PERFECT Works</h1>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex space-x-1 items-center">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`font-bold text-sm uppercase tracking-wider transition-all duration-300 px-3 py-2 rounded-md ${activeTab === item.id
                    ? 'bg-brand-blue text-white shadow-sm'
                    : 'text-[#475569] hover:bg-slate-100 hover:text-brand-blue'
                    }`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => handleNavClick('contact')}
                className="ml-4 bg-brand-lime hover:bg-[#8ab802] text-brand-blue text-sm font-black uppercase tracking-wider px-5 py-2.5 rounded shadow transition-colors"
              >
                Get a Quote
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-[#0d2786] hover:text-[#99cc02] focus:outline-none p-2 bg-slate-100 rounded-md transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-200 absolute w-full shadow-xl">
            <div className="px-4 py-2 space-y-1 max-h-[80vh] overflow-y-auto">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`block w-full text-left px-4 py-3 font-bold rounded-md mb-1 transition-colors ${activeTab === item.id ? 'bg-[#0d2786] text-[#99cc02]' : 'text-[#475569] hover:bg-slate-100'
                    }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content Area */}
      <main className="flex-grow">
        {activeTab === 'home' && <HomeTab setActiveTab={setActiveTab} />}
        {activeTab === 'about' && <AboutTab />}
        {activeTab === 'capabilities' && <CapabilitiesTab />}
        {activeTab === 'industries' && <IndustriesTab setActiveTab={setActiveTab} />}
        {activeTab === 'products' && <ProductsTab />}
        {activeTab === 'quality' && <QualityTab />}
        {activeTab === 'contact' && <ContactTab />}
      </main>

      {/* Footer */}
      <footer className="bg-brand-blue text-slate-300 pt-16 pb-8 border-t-4 border-brand-lime">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div className="lg:col-span-2">
              <div className="mb-6">
                <img src={logo} alt="B J Perfect Works" className="h-10 w-auto" />
              </div>
              <h2 className="text-xl font-bold text-brand-lime mb-4">Leading CNC Machining India</h2>
              <p className="leading-relaxed mb-6 max-w-md text-sm">
                Since 1987, BJ Perfect Works has been a trusted global precision machining supplier. We manufacture high-tolerance CNC turned parts, focusing on oil & gas machining components and automotive assemblies for OEMs worldwide.
              </p>
              <div className="flex space-x-4">
                <a href="/IATF_Certificate.pdf" target="_blank" rel="noopener noreferrer" title="Download IATF Certificate" className="group flex items-center text-brand-blue bg-brand-lime hover:bg-white transition-colors font-bold text-xs px-3 py-1 rounded shadow-sm">
                  <Download className="w-3.5 h-3.5 mr-1.5 group-hover:-translate-y-0.5 transition-transform" />
                  ISO/IATF 16949:2016
                </a>
                <span className="text-white font-semibold text-xs border border-brand-lime px-3 py-1 rounded flex items-center">Export: USA & EU</span>
              </div>
            </div>

            <div>
              <h3 className="text-white font-bold mb-6 uppercase tracking-wider text-sm">Capabilities</h3>
              <ul className="space-y-3 text-sm">
                <li><button onClick={() => handleNavClick('capabilities')} className="hover:text-brand-lime transition flex items-center"><ChevronRight className="w-3 h-3 mr-2" /> CNC Turning</button></li>
                <li><button onClick={() => handleNavClick('capabilities')} className="hover:text-brand-lime transition flex items-center"><ChevronRight className="w-3 h-3 mr-2" /> VMC Machining</button></li>
                <li><button onClick={() => handleNavClick('products')} className="hover:text-brand-lime transition flex items-center"><ChevronRight className="w-3 h-3 mr-2" /> High Pressure Mandrels</button></li>
                <li><button onClick={() => handleNavClick('products')} className="hover:text-brand-lime transition flex items-center"><ChevronRight className="w-3 h-3 mr-2" /> SS Machined Parts</button></li>
                <li><button onClick={() => handleNavClick('industries')} className="hover:text-brand-lime transition flex items-center"><ChevronRight className="w-3 h-3 mr-2" /> Oil & Gas Components</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-6 uppercase tracking-wider text-sm">Contact</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start">
                  <MapPin className="w-4 h-4 text-brand-lime mr-3 mt-0.5 flex-shrink-0" />
                  <span>E-12-17(8), E Block, MIDC, Bhosari, Pune - 411026, Maharashtra, India</span>
                </li>
                <li className="flex items-center">
                  <Phone className="w-4 h-4 text-brand-lime mr-3 flex-shrink-0" />
                  <span>+91 98500 99243</span>
                </li>
                <li className="flex items-center">
                  <Mail className="w-4 h-4 text-brand-lime mr-3 flex-shrink-0" />
                  <span>enquiry@bjperfectworks.com</span>
                </li>
                <li className="flex items-center">
                  <Linkedin className="w-4 h-4 text-brand-lime mr-3 flex-shrink-0" />
                  <a href="https://linkedin.com/company/bj-perfect-works" target="_blank" rel="noopener noreferrer" className="hover:text-white transition break-all">
                    linkedin.com/company/bj-perfect-works
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-8 flex flex-col md:flex-row justify-between items-center text-xs">
            <p>&copy; {new Date().getFullYear()} B J Perfect Works. All rights reserved.</p>
            <p className="mt-2 md:mt-0 text-brand-lime">Premium Precision Machining Supplier | Pune, India</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// --- TAB COMPONENTS ---

function HomeTab({ setActiveTab }) {
  const shopfloor = require('./assets/shopfloor.jpg');
  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <div className="relative bg-brand-blue text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1565514020179-026b92b84bb6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
            alt="Advanced CNC Machining Process"
            className="w-full h-full object-cover opacity-30 mix-blend-overlay"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/80 to-transparent"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32 lg:py-48">
          <div className="max-w-3xl">
            <div className="inline-flex items-center bg-[#99cc02] text-[#0d2786] px-4 py-1.5 rounded-full text-xs font-black mb-6 tracking-wide shadow-lg">
              <Globe className="w-3.5 h-3.5 mr-2" /> Global Precision Manufacturing Partner
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight text-white">
              Precision Machining Supplier for <span className="text-[#99cc02]">Global OEMs</span>
            </h1>
            <p className="text-lg md:text-xl text-blue-100 mb-10 leading-relaxed max-w-2xl font-light">
              Since 1987, BJ Perfect Works has delivered high-tolerance CNC and VMC machined components to the Oil & Gas, Automotive, and Industrial sectors across geographies.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setActiveTab('contact')}
                className="bg-brand-lime hover:bg-[#8ab802] text-brand-blue px-8 py-4 rounded font-black transition shadow-lg text-center tracking-wide"
              >
                Request a Quote
              </button>
              <button
                onClick={() => setActiveTab('capabilities')}
                className="bg-white/10 hover:bg-brand-lime hover:text-brand-blue text-white border border-brand-lime px-8 py-4 rounded font-bold transition flex items-center justify-center backdrop-blur-sm"
              >
                Explore Capabilities <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Bar */}
      <div className="bg-white border-b border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-x divide-slate-100">
            <div>
              <div className="text-3xl font-black text-[#0d2786] mb-1">1987</div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Established</div>
            </div>
            <div>
              <div className="text-3xl font-black text-[#0d2786] mb-1">ISO/IATF</div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">16949:2016 Certified</div>
            </div>
            <div>
              <div className="text-3xl font-black text-[#0d2786] mb-1">Automotive/ Oil & gas</div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Primary Export Market</div>
            </div>
            <div>
              <div className="text-3xl font-black text-[#0d2786] mb-1">Zero</div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Defect Tolerance</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Overview Sections */}
      <div className="py-20 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h2 className="text-sm font-black text-[#99cc02] tracking-widest uppercase mb-2">Company Overview</h2>
              <h3 className="text-3xl md:text-4xl font-extrabold text-[#0d2786] mb-6">Engineering Excellence Built on Decades of Trust.</h3>
              <p className="text-[#475569] mb-6 leading-relaxed">
                We are a premier CNC turned parts manufacturer based in India, specializing in complex geometries and tight tolerances. From high-pressure mandrels to custom automotive sub-assemblies, our infrastructure is designed to act as a seamless extension of your manufacturing floor.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-[#334155] font-bold"><CheckCircle className="w-5 h-5 mr-3 text-[#99cc02]" /> State-of-the-art CNC & VMC Centers</li>
                <li className="flex items-center text-[#334155] font-bold"><CheckCircle className="w-5 h-5 mr-3 text-[#99cc02]" /> Specialized in Stainless Steel Machining</li>
                <li className="flex items-center text-[#334155] font-bold"><CheckCircle className="w-5 h-5 mr-3 text-[#99cc02]" /> Strict adherence to global OEM specifications</li>
              </ul>
              <button onClick={() => setActiveTab('about')} className="text-[#0d2786] font-black hover:text-[#99cc02] flex items-center transition">
                Read our full history <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
            <div className="relative">
              {/*<div className="absolute inset-0 bg-[#99cc02] rounded-2xl transform translate-x-4 translate-y-4"></div>*/}
              <img src={shopfloor} alt="BJ Perfect Works Factory" className="relative rounded-2xl shadow-xl w-full object-cover h-80" />
            </div>
          </div>

          {/* Core Capabilities Preview */}
          <div className="mb-16 text-center">
            <h2 className="text-3xl font-extrabold text-brand-blue mb-4">Core Manufacturing Capabilities</h2>
            <div className="w-16 h-1.5 bg-brand-lime mx-auto mb-10 rounded"></div>

            <div className="grid md:grid-cols-3 gap-8 text-left">
              <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:border-brand-lime hover:shadow-md transition group">
                <Settings className="w-10 h-10 text-brand-lime mb-6 group-hover:rotate-90 transition-transform duration-500" />
                <h3 className="text-xl font-bold text-brand-blue mb-3">CNC Turning</h3>
                <p className="text-[#64748b] mb-4 text-sm leading-relaxed">High-volume, high-precision turned parts using advanced CNC Lathes (Chuck Dia: 135 to 300 mm ) capable of machining diverse super-alloys and steel.</p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:border-brand-lime hover:shadow-md transition group">
                <Wrench className="w-10 h-10 text-brand-lime mb-6" />
                <h3 className="text-xl font-bold text-brand-blue mb-3">VMC Machining</h3>
                <p className="text-[#64748b] mb-4 text-sm leading-relaxed">Complex milling operations delivering exceptional surface finishes and dimensional accuracy for intricate OEM components.</p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:border-brand-lime hover:shadow-md transition group">
                <ShieldCheck className="w-10 h-10 text-brand-lime mb-6" />
                <h3 className="text-xl font-bold text-brand-blue mb-3">Custom SS Machining</h3>
                <p className="text-[#64748b] mb-4 text-sm leading-relaxed">Expertise in machining various grades of stainless steel to prevent corrosion in harsh oil, gas, and fluid environments.</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

function AboutTab() {
  const chuck = require('./assets/chuck.png');
  const parts = require('./assets/Parts.png');
  return (
    <div className="animate-fade-in bg-white pb-20">
      <div className="bg-brand-blue py-20 text-center text-white relative overflow-hidden border-b-4 border-brand-lime">
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1504917595217-d4f3915ce113?auto=format&fit=crop&q=80')] bg-cover bg-center"></div>
        <div className="relative z-10">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 uppercase tracking-tight">About Us</h1>
          <p className="text-brand-lime max-w-2xl mx-auto text-lg font-bold">A legacy of precision since 1987.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <h2 className="text-3xl font-bold text-brand-blue mb-6">Our History</h2>
            <p className="text-[#475569] mb-4 leading-relaxed">
              Founded in <strong className="text-brand-blue">1987</strong>, B. J. Perfect Works began its journey with conventional machining equipment, dedicating itself to the fundamental principles of mechanical engineering. Over the decades, we have aggressively reinvested in technology, transitioning our infrastructure into a fully modernized CNC and VMC manufacturing powerhouse. We Specialize in working with Sintered components.
            </p>
            <p className="text-[#475569] mb-6 leading-relaxed">
              Today, operating out of the Bhosari MIDC in Pune, we are recognized as a premier <strong className="text-brand-blue">precision machining supplier</strong>. Our evolution reflects the changing demands of global industries—from rudimentary parts to highly complex, mission-critical components exported heavily to the United States and other global markets.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={parts} alt="Historical Engineering" className="rounded-lg shadow-md w-full h-48 object-cover " />
            <img src={chuck} alt="Modern Facility" className="rounded-lg shadow-md w-full h-48 object-cover translate-y-8 " />
          </div>
        </div>

        <div className="bg-[#f8fafc] rounded-2xl p-8 md:p-12 border border-slate-200">
          <h2 className="text-2xl font-black text-brand-blue mb-8 text-center uppercase tracking-wide">Our Core Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-brand-blue w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg text-brand-lime border-2 border-brand-lime">
                <Target className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-brand-blue mb-2">Precision</h3>
              <p className="text-[#64748b] text-sm leading-relaxed">Achieving the tightest tolerances requested by engineering blueprints, ensuring flawless fit and function in final assemblies.</p>
            </div>
            <div className="text-center">
              <div className="bg-brand-blue w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg text-brand-lime border-2 border-brand-lime">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-brand-blue mb-2">Quality</h3>
              <p className="text-[#64748b] text-sm leading-relaxed">An uncompromising stance on material integrity and dimensional accuracy, backed by our ISO/IATF 16949:2016 certification.</p>
            </div>
            <div className="text-center">
              <div className="bg-brand-blue w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg text-brand-lime border-2 border-brand-lime">
                <Award className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-brand-blue mb-2">Reliability</h3>
              <p className="text-[#64748b] text-sm leading-relaxed">Building long-term B2B partnerships through consistent on-time delivery and scalable manufacturing capacity.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CapabilitiesTab() {
  return (
    <div className="animate-fade-in bg-slate-50 pb-20">
      <div className="bg-[#0d2786] py-20 text-center text-white relative border-b-4 border-[#99cc02]">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 uppercase tracking-tight">Capabilities</h1>
        <p className="text-[#99cc02] max-w-2xl mx-auto text-lg font-bold">Advanced machining for complex engineering requirements.</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

          <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:border-[#99cc02] hover:shadow-md transition-colors">
            <div className="flex items-center mb-4 pb-4 border-b border-slate-100">
              <Settings className="w-8 h-8 text-[#0d2786] mr-4" />
              <h2 className="text-2xl font-bold text-[#0d2786]">CNC Turning</h2>
            </div>
            <p className="text-[#475569] mb-4">
              We operate a fleet of high-speed, multi-axis CNC turning centers capable of continuous mass production. We handle varying diameters and lengths, providing excellent concentricity and surface finishes.
            </p>
            <ul className="text-sm font-semibold text-[#64748b] space-y-2">
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> Shafts, Spindles, and Connectors</li>
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> High-volume automated bar feeding</li>
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> Tight tolerance diameter turning</li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:border-[#99cc02] hover:shadow-md transition-colors">
            <div className="flex items-center mb-4 pb-4 border-b border-slate-100">
              <Wrench className="w-8 h-8 text-[#0d2786] mr-4" />
              <h2 className="text-2xl font-bold text-[#0d2786]">VMC Machining</h2>
            </div>
            <p className="text-[#475569] mb-4">
              Our Vertical Machining Centers (VMC) perform complex 3D milling, drilling, and tapping operations. Ideal for intricate geometries required by automotive blocks and industrial valve bodies.
            </p>
            <ul className="text-sm font-semibold text-[#64748b] space-y-2">
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> 3-Axis and 4-Axis capabilities</li>
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> Custom tooling profiles</li>
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> Castings and forgings machining</li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:border-[#99cc02] hover:shadow-md transition-colors">
            <div className="flex items-center mb-4 pb-4 border-b border-slate-100">
              <Ruler className="w-8 h-8 text-[#0d2786] mr-4" />
              <h2 className="text-2xl font-bold text-[#0d2786]">Precision Machining</h2>
            </div>
            <p className="text-[#475569] mb-4">
              The core of our business is achieving micrometer-level precision. We utilize specialized grinding and honing techniques post-machining to meet the exact surface roughness and dimensional specs required.
            </p>
            <ul className="text-sm font-semibold text-[#64748b] space-y-2">
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> Cylindrical and surface grinding</li>
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> High-pressure sealing surfaces</li>
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> Deburring and polishing</li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:border-[#99cc02] hover:shadow-md transition-colors">
            <div className="flex items-center mb-4 pb-4 border-b border-slate-100">
              <ShieldCheck className="w-8 h-8 text-[#0d2786] mr-4" />
              <h2 className="text-2xl font-bold text-[#0d2786]">Stainless Steel Machining</h2>
            </div>
            <p className="text-[#475569] mb-4">
              Stainless steel requires specific tooling and cooling strategies to prevent work hardening. We have extensive experience machining various SS grades (304, 316, 410, Duplex) for harsh environments.
            </p>
            <ul className="text-sm font-semibold text-[#64748b] space-y-2">
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> Corrosion-resistant component manufacturing</li>
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> Food-grade and Oil-grade steel handling</li>
              <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#99cc02] rounded-full mr-2"></div> Passivation and surface treatment</li>
            </ul>
          </div>

          <div className="md:col-span-2 bg-[#0d2786] text-white p-8 rounded-xl shadow-lg flex flex-col md:flex-row items-center justify-between border-l-4 border-[#99cc02]">
            <div className="mb-6 md:mb-0 md:mr-8">
              <h2 className="text-2xl font-bold mb-2 text-[#99cc02]">Custom Component Manufacturing</h2>
              <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
                We work directly with OEM engineers and procurement teams to turn CAD drawings into reality. Whether you need prototype development or build-to-print mass production, we act as your dedicated manufacturing partner.
              </p>
            </div>
            <Factory className="w-16 h-16 text-[#99cc02] opacity-80 flex-shrink-0" />
          </div>

        </div>
      </div>
    </div>
  );
}

function IndustriesTab({ setActiveTab }) {
  const oilgas_coverimg = require('./assets/oil&gas_cover.jpg');
  const automotive_coverimg = require('./assets/automotive_cover.avif');
  const pump_coverimg = require('./assets/pump.avif');
  return (
    <div className="animate-fade-in bg-white pb-20">
      <div className="bg-brand-blue py-20 text-center text-white border-b-4 border-brand-lime">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 uppercase tracking-tight">Industries Served</h1>
        <p className="text-brand-lime max-w-2xl mx-auto text-lg font-bold">Delivering critical components to global heavy industries.</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-16">
        <div className="grid md:grid-cols-2 gap-12">

          {/* Oil & Gas */}
          <div className="group relative overflow-hidden rounded-2xl shadow-lg bg-white border border-slate-200">
            <div className="h-64 overflow-hidden relative">
              <img src={oilgas_coverimg} alt="Oil and Gas" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue to-transparent opacity-90"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <HardHat className="w-8 h-8 mb-2 text-brand-lime" />
                <h2 className="text-3xl font-bold">Oil & Gas</h2>
              </div>
            </div>
            <div className="p-8">
              <p className="text-[#475569] mb-4">
                We produce critical Oil & Gas components built to withstand extreme pressures and corrosive downhole environments. Our US clients rely on us for seamless extraction and drilling parts.
              </p>
              <ul className="text-sm font-bold text-brand-blue space-y-2 mb-6">
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Artificial Lift Systems</li>
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Wellhead Equipment</li>
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Flow Control Valves</li>
              </ul>
            </div>
          </div>

          {/* Automotive */}
          <div className="group relative overflow-hidden rounded-2xl shadow-lg bg-white border border-slate-200">
            <div className="h-64 overflow-hidden relative">
              <img src={automotive_coverimg} />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue to-transparent opacity-90"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <Cog className="w-8 h-8 mb-2 text-brand-lime" />
                <h2 className="text-3xl font-bold">Automotive</h2>
              </div>
            </div>
            <div className="p-8">
              <p className="text-[#475569] mb-4">
                Partnering with automotive component companies to deliver high-volume, defect-free turned parts and mechanical sub-assemblies for both commercial and passenger vehicles.
              </p>
              <ul className="text-sm font-bold text-brand-blue space-y-2 mb-6">
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Transmission Components</li>
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Engine Blocks & Spindles</li>
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Steering System Parts</li>
              </ul>
            </div>
          </div>

          {/* Pump & Fluid Systems */}
          <div className="group relative overflow-hidden rounded-2xl shadow-lg bg-white border border-slate-200">
            <div className="h-64 overflow-hidden relative">
              <img src={pump_coverimg} />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue to-transparent opacity-90"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <Cylinder className="w-8 h-8 mb-2 text-brand-lime" />
                <h2 className="text-3xl font-bold">Pump & Fluid Systems</h2>
              </div>
            </div>
            <div className="p-8">
              <p className="text-[#475569] mb-4">
                Supplying global pump manufacturers with precision machined internals. We ensure perfect concentricity and sealing surfaces for high-pressure fluid transfer applications.
              </p>
              <ul className="text-sm font-bold text-brand-blue space-y-2 mb-6">
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> High Pressure Mandrels & Plungers</li>
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Impellers & Housings</li>
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Fluid Control Manifolds</li>
              </ul>
            </div>
          </div>

          {/* Industrial Machinery */}
          <div className="group relative overflow-hidden rounded-2xl shadow-lg bg-white border border-slate-200">
            <div className="h-64 overflow-hidden relative">
              <img src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&w=1000&q=80" alt="Industrial Machinery" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue to-transparent opacity-90"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <Factory className="w-8 h-8 mb-2 text-brand-lime" />
                <h2 className="text-3xl font-bold">Industrial Machinery</h2>
              </div>
            </div>
            <div className="p-8">
              <p className="text-[#475569] mb-4">
                Heavy-duty component manufacturing for general industrial equipment, material handling machinery, and custom OEM mechanical builds.
              </p>
              <ul className="text-sm font-bold text-brand-blue space-y-2 mb-6">
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Power Transmission Shafts</li>
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Custom Gear Blanks</li>
                <li className="flex items-center"><ChevronRight className="w-4 h-4 mr-1 text-brand-lime" /> Heavy Structural Fasteners</li>
              </ul>
            </div>
          </div>

        </div>

        <div className="mt-16 text-center">
          <button onClick={() => setActiveTab('contact')} className="bg-brand-lime hover:bg-[#8ab802] text-brand-blue px-8 py-4 rounded font-black shadow-lg transition uppercase tracking-wider text-sm">
            Discuss Your OEM Requirements
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductsTab() {
  const mandrell = require('./assets/Mandrell.png');
  const plungers = require('./assets/plungers.png');
  const ss = require('./assets/SS.png');
  const general_parts = require('./assets/general_prts.png');
  return (
    <div className="animate-fade-in bg-slate-50 pb-20">
      <div className="bg-brand-blue py-20 text-center text-white border-b-4 border-brand-lime">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 uppercase tracking-tight">Our Products</h1>
        <p className="text-brand-lime max-w-2xl mx-auto text-lg font-bold">Custom engineered solutions manufactured to exact blueprints.</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-16">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

          {/* Product Card 1 */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden group hover:border-brand-lime hover:shadow-md transition">
            <div className="h-48 bg-slate-100 relative overflow-hidden border-b border-slate-200">
              {/* Replace src with your actual image path */}
              <img src={general_parts} alt="Precision Machined Components" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
            </div>
            <div className="p-8">
              <div className="flex items-start mb-4">
                <div className="bg-slate-100 p-3 rounded-lg mr-4"><Settings className="w-6 h-6 text-brand-blue" /></div>
                <div>
                  <h3 className="text-2xl font-bold text-brand-blue mb-2">Precision Machined Components</h3>
                  <p className="text-[#64748b] text-sm">General purpose, high-tolerance parts including shafts, pins, bushings, and customized mechanical hardware tailored to client specifications.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product Card 2 */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden group hover:border-brand-lime hover:shadow-md transition">
            <div className="h-48 bg-slate-100 relative overflow-hidden border-b border-slate-200">
              {/* Replace src with your actual image path */}
              <img src={mandrell} />
            </div>
            <div className="p-8">
              <div className="flex items-start mb-4">
                <div className="bg-slate-100 p-3 rounded-lg mr-4"><Cylinder className="w-6 h-6 text-brand-blue" /></div>
                <div>
                  <h3 className="text-2xl font-bold text-brand-blue mb-2">High Pressure Mandrels</h3>
                  <p className="text-[#64748b] text-sm">Critical components for downhole tooling and fluid systems. Machined from high-strength alloys to withstand extreme compression and tension without failure.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product Card 3 */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden group hover:border-brand-lime hover:shadow-md transition">
            <div className="h-48 bg-slate-100 relative overflow-hidden border-b border-slate-200">
              {/* Replace src with your actual image path */}
              <img src={plungers} />
            </div>
            <div className="p-8">
              <div className="flex items-start mb-4">
                <div className="bg-slate-100 p-3 rounded-lg mr-4"><Cog className="w-6 h-6 text-brand-blue" /></div>
                <div>
                  <h3 className="text-2xl font-bold text-brand-blue mb-2">Industrial Plungers</h3>
                  <p className="text-[#64748b] text-sm">Engineered for artificial lift systems and high-pressure pumps. We ensure flawless surface finish and concentricity for optimal seal life and performance.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product Card 4 */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden group hover:border-brand-lime hover:shadow-md transition">
            <div className="h-48 bg-slate-100 relative overflow-hidden border-b border-slate-200">
              {/* Replace src with your actual image path */}
              <img src={ss} alt="Stainless Steel Parts" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
            </div>
            <div className="p-8">
              <div className="flex items-start mb-4">
                <div className="bg-slate-100 p-3 rounded-lg mr-4"><ShieldCheck className="w-6 h-6 text-brand-blue" /></div>
                <div>
                  <h3 className="text-2xl font-bold text-brand-blue mb-2">Stainless Steel Parts</h3>
                  <p className="text-[#64748b] text-sm">Specialized SS fittings, connectors, and bodies for environments requiring high corrosion resistance, primarily targeting the Oil & Gas and fluid transport sectors.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-brand-blue text-white p-8 rounded-xl text-center border border-brand-lime">
          <Wrench className="w-12 h-12 mx-auto text-brand-lime mb-4" />
          <h3 className="text-2xl font-bold mb-4 text-brand-lime">Custom Engineered Components</h3>
          <p className="text-slate-300 max-w-3xl mx-auto">
            We do not manufacture off-the-shelf retail products. 100% of our output consists of B2B custom engineered components created strictly based on OEM blueprints, materials specs, and NDAs.
          </p>
        </div>

      </div>
    </div>
  );
}

function QualityTab() {
  const comp = require("./assets/comp.jpeg");
  return (
    <div className="animate-fade-in bg-white pb-20">
      <div className="bg-brand-blue py-20 text-center text-white border-b-4 border-brand-lime">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 uppercase tracking-tight">Quality & Infrastructure</h1>
        <p className="text-brand-lime max-w-2xl mx-auto text-lg font-bold">Zero defect tolerance backed by world-class machinery.</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-16">

        <div className="mb-16 rounded-2xl overflow-hidden shadow-xl border border-slate-200">
          <img src={comp} alt="BJ Perfect Works Facility" className="w-full h-80 object-cover" />
          <div className="bg-white p-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center divide-x divide-slate-100 border-t border-slate-200">
            <div><div className="text-brand-blue font-black">Location</div><div className="text-xs text-[#64748b] font-bold">Pune, India</div></div>
            <div><div className="text-brand-blue font-black">Certification</div><div className="text-xs text-[#64748b] font-bold">ISO/IATF 16949:2016</div></div>
            <div><div className="text-brand-blue font-black">Operations</div><div className="text-xs text-[#64748b] font-bold">24/7 Production</div></div>
            <div><div className="text-brand-blue font-black">Export</div><div className="text-xs text-[#64748b] font-bold">USA, EU, Global</div></div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

          <div>
            <h2 className="text-2xl font-bold text-brand-blue mb-6 flex items-center border-b border-slate-200 pb-3">
              <Settings className="w-6 h-6 mr-3 text-brand-lime" /> Machinery Arsenal
            </h2>
            <p className="text-[#475569] mb-6 text-sm leading-relaxed">
              Our shop floor is equipped with late-model CNC turning centers, Vertical Machining Centers (VMCs), and precision grinding equipment. Regular calibration and predictive maintenance ensure our machines hold the tightest tolerances across long production runs.
            </p>
            <ul className="space-y-3 font-semibold">
              <li className="flex items-center text-brand-blue bg-slate-50 p-3 rounded border border-slate-100">
                <div className="w-2.5 h-2.5 bg-brand-lime rounded-full mr-3"></div> CNC Lathes (Chuck Dia: 135 to 300 mm )
              </li>
              <li className="flex items-center text-brand-blue bg-slate-50 p-3 rounded border border-slate-100">
                <div className="w-2.5 h-2.5 bg-brand-lime rounded-full mr-3"></div> 3 & 4 Axis VMC Mills
              </li>
              <li className="flex items-center text-brand-blue bg-slate-50 p-3 rounded border border-slate-100">
                <div className="w-2.5 h-2.5 bg-brand-lime rounded-full mr-3"></div> Centerless Grinders & Automats
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-brand-blue mb-6 flex items-center border-b border-slate-200 pb-3">
              <Microscope className="w-6 h-6 mr-3 text-brand-lime" /> Quality Control
            </h2>
            <p className="text-[#475569] mb-6 text-sm leading-relaxed">
              Quality is engineered into every step. As an ISO/IATF 16949:2016 certified facility, we maintain a climate-controlled inspection lab. Parts undergo strict first-article inspection, in-process sampling, and final pre-shipment auditing.
            </p>
            <div className="mb-6">
              <a
                href="/IATF_Certificate.pdf"
                target="_blank"
                rel="noopener noreferrer"
                title="Download ISO/IATF 16949:2016 Certificate"
                className="inline-flex items-center px-5 py-2.5 bg-brand-blue hover:bg-[#0a1e66] text-white text-sm font-bold rounded shadow-md transition-all group"
              >
                <Download className="w-4 h-4 mr-2 text-brand-lime group-hover:-translate-y-0.5 transition-transform" />
                Download IATF Certificate
              </a>
            </div>
            <ul className="space-y-3 font-semibold">
              <li className="flex items-center text-brand-blue bg-slate-50 p-3 rounded border border-slate-100">
                <div className="w-2.5 h-2.5 bg-brand-lime rounded-full mr-3"></div> Contracer
              </li>
              <li className="flex items-center text-brand-blue bg-slate-50 p-3 rounded border border-slate-100">
                <div className="w-2.5 h-2.5 bg-brand-lime rounded-full mr-3"></div> Digital Height Gauges & Micrometers
              </li>
              <li className="flex items-center text-brand-blue bg-slate-50 p-3 rounded border border-slate-100">
                <div className="w-2.5 h-2.5 bg-brand-lime rounded-full mr-3"></div> Surface Roughness Testers
              </li>
            </ul>
          </div>

          <div className="md:col-span-2 bg-brand-blue text-white p-8 rounded-xl shadow-lg mt-4 border-l-4 border-brand-lime">
            <h2 className="text-2xl font-bold mb-4 text-brand-lime">Our Skilled Workforce</h2>
            <p className="text-slate-300 leading-relaxed">
              Machines alone do not guarantee precision. Our greatest asset is our workforce of highly trained machinists, CNC programmers, and quality engineers. Cultivated through ongoing training and decades of retained company knowledge since 1987, our team operates with a problem-solving mindset, ensuring complex drawings are translated flawlessly into physical parts.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}

function ContactTab() {
  const [result, setResult] = useState("");

  const onSubmit = async (event) => {
    event.preventDefault();
    setResult("Sending...");

    const formData = new FormData(event.target);

    // Web3Forms Access Key - GET YOURS AT https://web3forms.com/
    formData.append("access_key", "bb134205-755c-451b-8571-eeae49f6a66b");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setResult("Inquiry sent successfully! We will contact you shortly.");
        event.target.reset();
      } else {
        console.log("Error", data);
        setResult("Something went wrong. Please try again or email us directly.");
      }
    } catch (error) {
      console.error(error);
      setResult("Something went wrong. Please check your connection.");
    }
  };

  return (
    <div className="animate-fade-in bg-slate-50 pb-20">
      <div className="bg-brand-blue py-20 text-center text-white border-b-4 border-brand-lime">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 uppercase tracking-tight">Contact Us</h1>
        <p className="text-brand-lime max-w-2xl mx-auto text-lg font-bold">Generate inquiries, request quotes, or plan an audit.</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Contact Details */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
              <h3 className="text-xl font-bold text-brand-blue mb-6 border-b border-slate-100 pb-4">Corporate Office & Works</h3>

              <div className="space-y-6">
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 text-brand-lime mt-1 flex-shrink-0 mr-4" />
                  <div>
                    <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider mb-1">Location in India</h4>
                    <p className="text-[#64748b] text-sm leading-relaxed">
                      E-12-17(8), E Block,<br />
                      MIDC, Bhosari,<br />
                      Pune – 411 026, Maharashtra, India.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="w-5 h-5 text-brand-lime mt-1 flex-shrink-0 mr-4" />
                  <div>
                    <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider mb-1">Phone</h4>
                    <p className="text-[#64748b] text-sm font-bold">+91 98500 99243</p>
                    <p className="text-[#64748b] text-sm font-bold">+91 98236 23094</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-brand-lime mt-1 flex-shrink-0 mr-4" />
                  <div>
                    <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider mb-1">Email</h4>
                    <p className="text-[#64748b] text-sm font-bold">enquiry@bjperfectworks.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-brand-blue p-8 rounded-xl shadow-sm text-white border-b-4 border-brand-lime">
              <h3 className="font-bold mb-2 text-brand-lime">Global Buyers</h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                We are highly experienced in logistics, packaging, and export compliance for delivering directly to facilities in the United States and Europe.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-8 md:p-10 rounded-xl shadow-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-brand-blue mb-8">Send a B2B Inquiry</h3>
              <form className="space-y-6" onSubmit={onSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-[#0d2786] uppercase tracking-wider mb-2">Full Name</label>
                    <input type="text" name="name" required className="w-full bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#99cc02] focus:bg-white transition" placeholder="John Doe" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#0d2786] uppercase tracking-wider mb-2">Company Name</label>
                    <input type="text" name="company" required className="w-full bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#99cc02] focus:bg-white transition" placeholder="OEM Corporation" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-[#0d2786] uppercase tracking-wider mb-2">Corporate Email</label>
                    <input type="email" name="email" required className="w-full bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#99cc02] focus:bg-white transition" placeholder="john@oemcorp.com" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#0d2786] uppercase tracking-wider mb-2">Phone / Country Code</label>
                    <input type="tel" name="phone" className="w-full bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#99cc02] focus:bg-white transition" placeholder="+1 (555) 000-0000" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#0d2786] uppercase tracking-wider mb-2">Inquiry Type</label>
                  <select name="inquiry_type" className="w-full bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#99cc02] focus:bg-white transition">
                    <option>Request a Quote (RFQ)</option>
                    <option>Oil & Gas Components Inquiry</option>
                    <option>Automotive / General Machining Inquiry</option>
                    <option>Facility Audit / Vendor Registration</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#0d2786] uppercase tracking-wider mb-2">Project Details / Message</label>
                  <textarea name="message" required rows="5" className="w-full bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#99cc02] focus:bg-white transition" placeholder="Please describe material specifications, estimated annual volumes, and attach drawing details..."></textarea>
                </div>

                <div className="flex items-center space-x-4">
                  <button type="submit" className="bg-brand-blue hover:bg-brand-lime hover:text-brand-blue text-white font-bold py-3 px-8 rounded shadow-md transition w-full md:w-auto uppercase tracking-wider text-sm">
                    Submit Inquiry
                  </button>
                  {result && (
                    <span className={`text-sm font-bold ${result.includes('successfully') ? 'text-brand-lime' : 'text-brand-blue'}`}>
                      {result}
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}